import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "../lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import { Seo } from "../components/Seo";
import { Building2, TrendingUp, Handshake, MapPin, Check, ArrowRight } from "lucide-react";

const money = (c) => `$${(c / 100).toLocaleString(undefined, { maximumFractionDigits: 0 })}`;

export default function Franchise() {
    const navigate = useNavigate();
    const [fees, setFees] = useState(null);
    const [form, setForm] = useState({ business_name: "", contact_name: "", email: "", phone: "", territory: "", experience: "" });
    const [busy, setBusy] = useState(false);
    const [done, setDone] = useState(false);

    useEffect(() => { api.get("/franchise/info").then(({ data }) => setFees(data.fees)).catch(() => {}); }, []);

    const apply = async () => {
        if (!form.business_name || !form.contact_name || !form.email.includes("@")) {
            toast.error("Business name, your name and a valid email are required");
            return;
        }
        setBusy(true);
        try {
            await api.post("/franchise/apply", form);
            setDone(true);
            toast.success("Application submitted!");
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Could not submit application");
        }
        setBusy(false);
    };

    const perks = [
        { icon: TrendingUp, t: "Recurring revenue", d: "Earn on every customer you activate — wireless is a monthly-recurring business." },
        { icon: Handshake, t: "Turnkey platform", d: "We run the network, billing, eSIM and apps. You focus on growing your market." },
        { icon: MapPin, t: "Your territory", d: "Build a local FlexLine presence and own the relationships in your area." },
    ];

    return (
        <div className="min-h-screen bg-cream text-zinc-900">
            <Seo title="FlexLine Mobile — Authorized Dealer Program" description="Become a FlexLine Mobile Authorized Dealer. Low entry, recurring wireless revenue, turnkey eSIM platform. Apply today." path="/franchise" />
            {/* Hero */}
            <div className="bg-[#4C1D95] text-white px-6 sm:px-12 pt-16 pb-14" data-testid="franchise-hero">
                <div className="max-w-4xl mx-auto">
                    <div className="inline-flex items-center gap-2 bg-white/10 rounded-sm px-3 py-1 text-xs font-mono uppercase tracking-widest text-[#C4B5FD] mb-5">
                        <Building2 className="w-3.5 h-3.5" /> Authorized Dealer Program
                    </div>
                    <h1 className="text-4xl sm:text-6xl font-light tracking-tight leading-[1.03]">
                        Own a piece of the
                        <br /><span className="font-serif-italic">wireless revolution.</span>
                    </h1>
                    <p className="mt-5 text-lg text-[#D8CCF5] max-w-2xl">
                        Launch your own FlexLine Mobile wireless business on our turnkey eSIM platform. Low startup cost,
                        recurring revenue, and full corporate support — no cell towers or stores required.
                    </p>
                    <Button onClick={() => document.getElementById("apply")?.scrollIntoView({ behavior: "smooth" })}
                        data-testid="franchise-apply-cta" className="mt-7 bg-[#D97706] text-white rounded-sm h-12 px-7">
                        Apply to become a dealer <ArrowRight className="w-4 h-4 ml-1" />
                    </Button>
                </div>
            </div>

            {/* Perks */}
            <div className="max-w-4xl mx-auto px-6 sm:px-12 py-14 grid sm:grid-cols-3 gap-6">
                {perks.map((p) => (
                    <div key={p.t} className="border border-[#DDD6FE] rounded-sm bg-paper p-5">
                        <div className="w-10 h-10 rounded-sm bg-[#F5F0FF] grid place-items-center mb-3"><p.icon className="w-5 h-5 text-[#4C1D95]" /></div>
                        <h3 className="font-medium">{p.t}</h3>
                        <p className="text-sm text-zinc-600 mt-1">{p.d}</p>
                    </div>
                ))}
            </div>

            {/* Investment */}
            {fees && (
                <div className="max-w-4xl mx-auto px-6 sm:px-12 pb-14" data-testid="franchise-fees">
                    <h2 className="font-display text-3xl font-light mb-5">Your investment</h2>
                    <div className="border border-[#DDD6FE] rounded-sm bg-paper divide-y divide-[#EFE9F8]">
                        <Row label="One-time dealer license fee" value={`${money(fees.license_fee_cents)}`} sub={`or ${fees.installments} monthly payments of ${money(fees.installment_cents)}`} />
                        <Row label="Onboarding & training (one-time)" value={money(fees.onboarding_fee_cents)} />
                        <Row label="Platform / technology fee" value={`${money(fees.tech_fee_cents)}/mo`} />
                        <Row label="Royalty on transactions" value={`${(fees.royalty_rate * 100).toFixed(0)}%`} />
                        <Row label="Brand / marketing fund" value={`${(fees.marketing_rate * 100).toFixed(0)}%`} />
                    </div>
                    <p className="text-xs text-zinc-500 mt-2">Fees are collected securely after your application is approved and you e-sign the Authorized Dealer agreement.</p>
                </div>
            )}

            {/* Application */}
            <div id="apply" className="bg-white border-t border-[#DDD6FE] px-6 sm:px-12 py-14">
                <div className="max-w-2xl mx-auto">
                    <h2 className="font-display text-3xl font-light mb-2">Apply now</h2>
                    <p className="text-sm text-zinc-600 mb-6">Tell us about you. Our team reviews every application personally.</p>
                    {done ? (
                        <div className="border border-brand-green/30 bg-brand-green/5 rounded-sm p-8 text-center" data-testid="franchise-applied">
                            <Check className="w-10 h-10 text-brand-green mx-auto mb-3" />
                            <h3 className="font-display text-2xl">Application received!</h3>
                            <p className="text-sm text-zinc-600 mt-2">We'll review it and be in touch by email. Once approved, sign in and head to your Dealer Portal to sign the agreement and get started.</p>
                            <Button onClick={() => navigate("/franchise/portal")} data-testid="franchise-goto-portal" className="mt-5 bg-[#4C1D95] text-white rounded-sm">Go to Dealer Portal</Button>
                        </div>
                    ) : (
                        <div className="space-y-3" data-testid="franchise-form">
                            <Input placeholder="Business name" value={form.business_name} onChange={(e) => setForm({ ...form, business_name: e.target.value })} data-testid="fr-business-name" />
                            <div className="grid sm:grid-cols-2 gap-3">
                                <Input placeholder="Your full name" value={form.contact_name} onChange={(e) => setForm({ ...form, contact_name: e.target.value })} data-testid="fr-contact-name" />
                                <Input placeholder="Email" type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="fr-email" />
                            </div>
                            <div className="grid sm:grid-cols-2 gap-3">
                                <Input placeholder="Phone" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} data-testid="fr-phone" />
                                <Input placeholder="Territory (city / metro / ZIP)" value={form.territory} onChange={(e) => setForm({ ...form, territory: e.target.value })} data-testid="fr-territory" />
                            </div>
                            <Textarea placeholder="Tell us about your business experience and why FlexLine" value={form.experience} onChange={(e) => setForm({ ...form, experience: e.target.value })} data-testid="fr-experience" />
                            <Button onClick={apply} disabled={busy} data-testid="fr-submit" className="bg-[#D97706] text-white rounded-sm h-11 w-full">
                                {busy ? "Submitting…" : "Submit application"}
                            </Button>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
}

const Row = ({ label, value, sub }) => (
    <div className="flex items-center justify-between px-5 py-4">
        <div><div className="text-sm font-medium">{label}</div>{sub && <div className="text-xs text-zinc-500">{sub}</div>}</div>
        <div className="font-display text-xl">{value}</div>
    </div>
);
